import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';

import type { Step } from 'types/Step';
import { toUrlSlug, fromUrlSlug } from 'utils/stepUrlUtils';

import useAnalytics from './useAnalytics';

interface UseRouterStepsParams<Data, Props> {
  steps: Array<Step<Data, Props>>;
  onStepChange?: (step: Step<Data, Props>) => void;
}

interface UseRouterStepsData<Data, Props> {
  currentStep: Step<Data, Props>;
  next: () => void;
  previous: () => void;
  setStep: (stepKey: string) => void;
}

function useRouterSteps<Data = unknown, Props = unknown>({
  steps,
  onStepChange,
}: UseRouterStepsParams<Data, Props>): UseRouterStepsData<Data, Props> {
  const { onClick } = useAnalytics();
  const { step: stepSlug } = useParams<{ step: string }>();
  const navigate = useNavigate();

  const location = useLocation();

  const currentStep = React.useMemo(() => {
    if (stepSlug) {
      const stepKey = fromUrlSlug(stepSlug);
      const found = steps.find(({ key }) => key === stepKey);
      if (found) return found;
    }
    return steps[0];
  }, [stepSlug, steps]);

  // Notify parent of step changes
  const prevStepRef = React.useRef(currentStep);
  React.useEffect(() => {
    if (prevStepRef.current?.key !== currentStep?.key) {
      onStepChange?.(currentStep);
      prevStepRef.current = currentStep;
    }
  }, [currentStep, onStepChange]);

  const handleSetStep = React.useCallback(
    (stepKey: string) => {
      const targetStep = steps.find(({ key }) => key === stepKey);
      if (!targetStep || targetStep.key === currentStep?.key) return;

      const { allowReviewCheckIn: _, ...forwardState } = (location.state as Record<string, unknown> ?? {});
      navigate(`../${toUrlSlug(stepKey)}${location.search}`, {
        state: forwardState,
      });
    },
    [steps, currentStep, navigate, location]
  );

  const next = React.useCallback(() => {
    if (currentStep?.next) {
      onClick(
        { category: 'Navigate', action: 'Next', label: currentStep.next },
        () => handleSetStep(currentStep.next)
      )();
    }
  }, [currentStep, handleSetStep, onClick]);

  const previous = React.useCallback(() => {
    if (currentStep?.previous) {
      onClick(
        { category: 'Navigate', action: 'Previous', label: currentStep.previous },
        () => handleSetStep(currentStep.previous),
      )();
    }
  }, [currentStep, handleSetStep, onClick]);

  return React.useMemo(
    () => ({
      currentStep,
      next,
      previous,
      setStep: handleSetStep,
    }),
    [currentStep, next, previous, handleSetStep]
  );
}

export default useRouterSteps;
